import React from 'react';

import { EntertainmentPlayerName } from '@/components/entertainment/EntertainmentPlayerName';
import { Button, Card } from '@/components/ui';
import {
  createEntertainmentCategory,
  deleteEntertainmentCategory,
  updateEntertainmentCategory,
} from '@/lib/entertainment/control-plane-service';
import { isEligibleEntertainmentParticipant } from '@/lib/roles';
import type { EntertainmentCategory, User } from '@/lib/types/core';

type Props = {
  users: Array<Pick<User, 'id' | 'name' | 'is_active' | 'roles'>>;
  categories?: EntertainmentCategory[];
};

const QUICK_WEIGHTS = [0, 0.25, 0.5, 0.75, 1];

function clampWeight(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, Math.round(value * 10_000) / 10_000));
}

function weightMapForUsers(
  users: Array<Pick<User, 'id'>>,
  category?: Pick<EntertainmentCategory, 'default_weights'> | null,
) {
  return Object.fromEntries(
    users.map((user) => [user.id, clampWeight(category?.default_weights?.[user.id] ?? 0)]),
  ) as Record<string, number>;
}

export function EntertainmentCategoryManager({ users, categories = [] }: Props) {
  const eligibleUsers = React.useMemo(
    () => users.filter(isEligibleEntertainmentParticipant),
    [users],
  );
  const [selectedCategoryId, setSelectedCategoryId] = React.useState('');
  const selectedCategory = categories.find((category) => category.id === selectedCategoryId) ?? null;
  const [name, setName] = React.useState('');
  const [weights, setWeights] = React.useState<Record<string, number>>(() => weightMapForUsers(eligibleUsers));
  const [message, setMessage] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setName(selectedCategory?.name ?? '');
    setWeights(weightMapForUsers(eligibleUsers, selectedCategory));
    setMessage(null);
  }, [eligibleUsers, selectedCategory?.id]);

  const setWeight = (userId: string, value: number) => {
    setWeights((previous) => ({ ...previous, [userId]: clampWeight(value) }));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const body = { name: name.trim(), weights };
      if (selectedCategory) {
        await updateEntertainmentCategory(selectedCategory.id, body);
      } else {
        await createEntertainmentCategory(body);
      }
      setMessage('Category saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedCategory) return;

    setSaving(true);
    setMessage(null);
    try {
      await deleteEntertainmentCategory(selectedCategory.id, {
        reason: 'Deleted from Entertainment category manager.',
      });
      setSelectedCategoryId('');
      setMessage('Category deleted.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <form onSubmit={save} className="space-y-4">
        <h2 className="text-sm font-bold text-aura-primary mb-3 uppercase tracking-widest">
          Entertainment Categories
        </h2>

        <label htmlFor="ent-existing-category" className="block text-xs text-text-muted mb-1.5">
          Existing Category
        </label>
        <select
          id="ent-existing-category"
          value={selectedCategoryId}
          onChange={(event) => setSelectedCategoryId(event.target.value)}
          className="w-full p-3 rounded-lg border border-border-subtle bg-surface text-text-main text-sm"
        >
          <option value="">New Category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <label htmlFor="ent-category-name" className="block text-xs text-text-muted mb-1.5">
          Category Name
        </label>
        <input
          id="ent-category-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="w-full p-3 rounded-lg border border-border-subtle bg-surface text-text-main text-sm"
        />

        <div className="divide-y divide-border-subtle">
          {eligibleUsers.map((user) => (
            <div key={user.id} className="py-3">
              <label htmlFor={`weight-${user.id}`} className="flex items-center justify-between gap-3 text-xs text-text-muted">
                <EntertainmentPlayerName user={user} />
                <span className="shrink-0 font-semibold">Weight</span>
              </label>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {QUICK_WEIGHTS.map((weight) => (
                  <button
                    key={weight}
                    type="button"
                    onClick={() => setWeight(user.id, weight)}
                    aria-pressed={(weights[user.id] ?? 0) === weight}
                    className="h-8 min-w-9 rounded-md border border-border-subtle bg-surface px-2 text-xs font-semibold text-text-muted shadow-sm transition-[background-color,border-color,color] hover:border-brand-primary/30 hover:bg-bg-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30 aria-pressed:border-brand-primary aria-pressed:text-brand-primary"
                  >
                    {weight}
                  </button>
                ))}
                <input
                  id={`weight-${user.id}`}
                  aria-label={`${user.name} weight`}
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={weights[user.id] ?? 0}
                  onChange={(event) => setWeight(user.id, Number(event.target.value))}
                  className="h-9 w-24 rounded-lg border border-border-subtle bg-surface px-2 text-sm font-semibold text-text-main focus:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" fullWidth className="text-white" disabled={saving || !name.trim()}>
            Save Category
          </Button>
          {selectedCategory && (
            <Button type="button" variant="danger" fullWidth disabled={saving} onClick={remove}>
              Delete Category
            </Button>
          )}
        </div>
      </form>
      {message && <div className="mt-3 text-sm font-semibold text-aura-primary">{message}</div>}
    </Card>
  );
}
